import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Services } from "@/components/Services";
import { PlanComparison } from "@/components/PlanComparison";
import { CleaningPersonalityQuiz } from "@/components/CleaningPersonalityQuiz";
import { HowItWorks } from "@/components/HowItWorks";
import { FAQ } from "@/components/FAQ";
import { FinalCTA } from "@/components/FinalCTA";
import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, Sparkles, ShieldCheck, Clock, Star, Columns, Zap, Calendar } from "lucide-react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLocation } from "@/lib/locations";

const BOOKING_URL = "/book";

const ServicesOverview = () => {
  const location = useLocation();

  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Home Cleaning",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Uplevel Cleaning",
      "telephone": "+19162995588",
      "url": "https://uplevelcleaning.com",
      "priceRange": "$$",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Serving Sacramento & Surrounding Areas",
        "addressLocality": location.name,
        "addressRegion": "CA",
        "postalCode": "95814",
        "addressCountry": "US"
      }
    },
    "areaServed": {
      "@type": "City",
      "name": location.name
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Cleaning Packages",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Standard Clean",
            "description": "Routine maintenance cleaning for busy households."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Deep Clean",
            "description": "Comprehensive 75-point restorative cleaning."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Move In/Out Clean",
            "description": "Detailed cleaning for real estate transitions."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Post Construction",
            "description": "Dust and debris removal after renovations."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Commercial Cleaning",
            "description": `Professional cleaning for offices and retail spaces in ${location.name}.`
          }
        }
      ]
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <script type="application/ld+json">
        {JSON.stringify(serviceSchema)}
      </script>
      <SEO 
        title={`Professional Cleaning Services in ${location.name} | Uplevel Cleaning`}
        description={`Explore our range of professional home cleaning services in ${location.name}. From standard maid services to deep cleaning and post-construction. Instant pricing & 200% guarantee.`}
        keywords={`cleaning services ${location.name}, house cleaning packages, deep clean ${location.name}, move out cleaning, post construction cleaning ${location.name}`}
        url="https://uplevelcleaning.com/services"
      />
      <Navbar />
      <main className="flex-1">
        {/* Services Hero */}
        <section className="relative py-20 lg:py-32 bg-primary text-primary-foreground overflow-hidden">
          <div className="absolute inset-0 z-0 opacity-20">
            <picture>
              <source media="(max-width: 768px)" srcSet="https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=800&q=80" />
              <img
                src="https://vibe.filesafe.space/1776798830316075366/assets/e87a8deb-ec6f-458c-876c-a5573e164d40.png?w=1920&q=80"
                alt="Professional house cleaning background"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </picture>
          </div>
          <div className="container relative z-10 text-center">
            <div className="inline-flex items-center gap-2 mb-6 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full font-bold text-sm">
              <Sparkles className="h-4 w-4" />
              {location.name}'s Most Trusted Cleaners
            </div>
            <h1 className="text-4xl md:text-6xl font-extrabold mb-6 leading-tight max-w-4xl mx-auto">
              House Cleaning Services Built Around Your Home
            </h1>
            <p className="text-xl opacity-90 mb-10 max-w-2xl mx-auto">
              Choose from standard cleaning, deep cleaning, move-in/move-out cleaning, apartment cleaning, and recurring service in {location.name}.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="bg-white text-primary hover:bg-white/90 hover:text-primary font-bold text-lg px-8 py-6 h-auto rounded-2xl shadow-xl w-full sm:w-auto">
                <a href={BOOKING_URL} aria-label="Get instant price from services overview">
                  <Zap className="mr-2 h-6 w-6 fill-primary/20" />
                  Get Instant Price
                </a>
              </Button>
              <Button 
                size="lg" 
                className="bg-white/10 border border-white/30 text-white hover:bg-white/20 hover:text-white font-bold text-lg px-8 py-6 h-auto rounded-2xl shadow-xl w-full sm:w-auto"
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('open-recommendation-engine'));
                }}
              >
                <Sparkles className="mr-2 h-6 w-6" />
                Help Me Choose
              </Button>
            </div>
            <div className="mt-8 flex items-center justify-center gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current text-yellow-400" />)}
              <span className="ml-2 text-sm font-medium opacity-80">1,500+ Happy {location.name} Locals</span>
            </div>
          </div>
        </section>

        {/* Quick Service Comparison */}
        <section id="compare" className="py-20 bg-background scroll-mt-20">
          <div className="container max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">Quick Service Comparison</h2>
              <p className="text-lg text-muted-foreground">Find the right fit for your home at a glance.</p>
            </div>
            {/* Desktop Table */}
            <div className="hidden md:block overflow-x-auto pb-4">
              <table className="w-full text-left border-collapse min-w-[600px]">
                <thead>
                  <tr className="border-b-2 border-border">
                    <th className="p-4 font-bold text-lg">Service</th>
                    <th className="p-4 font-bold text-lg">Best For</th>
                    <th className="p-4 font-bold text-lg">Recommended When</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr className="hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-bold text-primary">Standard Cleaning</td>
                    <td className="p-4 text-muted-foreground">Maintenance</td>
                    <td className="p-4">Home is already fairly clean</td>
                  </tr>
                  <tr className="hover:bg-muted/30 transition-colors bg-primary/5">
                    <td className="p-4 font-bold text-primary">Deep Cleaning</td>
                    <td className="p-4 text-muted-foreground font-medium">First-time or detailed cleaning</td>
                    <td className="p-4 font-medium">Home needs a reset</td>
                  </tr>
                  <tr className="hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-bold text-primary">Move-Out Cleaning</td>
                    <td className="p-4 text-muted-foreground">Empty homes</td>
                    <td className="p-4">Moving in or out</td>
                  </tr>
                  <tr className="hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-bold text-primary">Recurring Cleaning</td>
                    <td className="p-4 text-muted-foreground">Ongoing maintenance</td>
                    <td className="p-4">Weekly, bi-weekly, or monthly</td>
                  </tr>
                  <tr className="hover:bg-muted/30 transition-colors">
                    <td className="p-4 font-bold text-primary">Apartment Cleaning</td>
                    <td className="p-4 text-muted-foreground">Smaller spaces</td>
                    <td className="p-4">Apartments and condos</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden grid grid-cols-1 gap-4">
              <div className="bg-background border rounded-xl p-4 shadow-sm hover:border-primary/50 transition-colors">
                <div className="font-bold text-primary text-lg mb-1">Standard Cleaning</div>
                <div className="text-sm text-muted-foreground mb-2">Best For: Maintenance</div>
                <div className="text-sm font-medium">Recommended: Home is already fairly clean</div>
              </div>
              <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 shadow-sm">
                <div className="font-bold text-primary text-lg mb-1">Deep Cleaning</div>
                <div className="text-sm text-muted-foreground font-medium mb-2">Best For: First-time or detailed cleaning</div>
                <div className="text-sm font-medium">Recommended: Home needs a reset</div>
              </div>
              <div className="bg-background border rounded-xl p-4 shadow-sm hover:border-primary/50 transition-colors">
                <div className="font-bold text-primary text-lg mb-1">Move-Out Cleaning</div>
                <div className="text-sm text-muted-foreground mb-2">Best For: Empty homes</div>
                <div className="text-sm font-medium">Recommended: Moving in or out</div>
              </div>
              <div className="bg-background border rounded-xl p-4 shadow-sm hover:border-primary/50 transition-colors">
                <div className="font-bold text-primary text-lg mb-1">Recurring Cleaning</div>
                <div className="text-sm text-muted-foreground mb-2">Best For: Ongoing maintenance</div>
                <div className="text-sm font-medium">Recommended: Weekly, bi-weekly, or monthly</div>
              </div>
              <div className="bg-background border rounded-xl p-4 shadow-sm hover:border-primary/50 transition-colors">
                <div className="font-bold text-primary text-lg mb-1">Apartment Cleaning</div>
                <div className="text-sm text-muted-foreground mb-2">Best For: Smaller spaces</div>
                <div className="text-sm font-medium">Recommended: Apartments and condos</div>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Service */}
        <section className="py-16 bg-primary/5 border-y border-primary/10">
          <div className="container max-w-4xl text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Most First-Time Customers Start With a Deep Clean</h2>
            <p className="text-lg text-muted-foreground mb-8">
              A deep clean helps reset the home and makes future recurring cleanings easier to maintain.
            </p>
            <Button asChild size="lg" className="font-bold text-lg px-8 py-6 rounded-xl shadow-lg">
              <a href={`${BOOKING_URL}?service=deep-clean`}>Book a Deep Clean</a>
            </Button>
          </div>
        </section>

        {/* Core Services Grid */}
        <Services />

        {/* Interactive Quiz */}
        <CleaningPersonalityQuiz />


        {/* How It Works */}
        <HowItWorks />

        {/* Which Service Should I Choose? */}
        <section className="py-24 bg-muted/20 border-t">
          <div className="container max-w-4xl">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">Which Service Should I Choose?</h2>
              <p className="text-lg text-muted-foreground">A quick guide to finding the perfect clean for your home.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-background rounded-2xl p-8 border shadow-sm">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" /> Choose Standard Cleaning if:
                </h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li>• Your home is already well-maintained</li>
                  <li>• You want recurring cleaning (weekly, bi-weekly)</li>
                </ul>
              </div>
              <div className="bg-primary/5 rounded-2xl p-8 border border-primary/20">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" /> Choose Deep Cleaning if:
                </h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li>• It is your first professional cleaning visit</li>
                  <li>• Your home needs more detailed attention</li>
                </ul>
              </div>
              <div className="bg-background rounded-2xl p-8 border shadow-sm">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" /> Choose Move-Out Cleaning if:
                </h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li>• The home is completely empty</li>
                  <li>• You are moving in or moving out</li>
                </ul>
              </div>
              <div className="bg-background rounded-2xl p-8 border shadow-sm">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-primary" /> Choose Recurring Cleaning if:
                </h3>
                <ul className="space-y-3 text-muted-foreground">
                  <li>• You want consistent, stress-free maintenance</li>
                  <li>• You want to save up to 35% on your cleans</li>
                </ul>
              </div>
            </div>
            <div className="mt-12 flex justify-center">
              <Button 
                size="lg" 
                className="font-bold text-lg px-8 py-6 h-auto rounded-2xl shadow-xl w-full sm:w-auto"
                onClick={(e) => {
                  e.preventDefault();
                  window.dispatchEvent(new CustomEvent('open-recommendation-engine'));
                }}
              >
                <Sparkles className="mr-2 h-6 w-6" />
                Help Me Choose
              </Button>
            </div>
          </div>
        </section>

        {/* Detailed Comparison */}
        <section className="py-24 bg-muted/30">
          <div className="container">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold mb-4">Compare Our Plans</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Not sure which service is right for you? See exactly what's included in each of our professional cleaning tiers.
              </p>
            </div>
            <PlanComparison />
          </div>
        </section>



        {/* Trust Pillars */}
        <section className="py-16 md:py-24 bg-background">
          <div className="container">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-12">
              <div className="flex flex-row md:flex-col items-center md:text-center p-4 md:p-8 rounded-2xl md:rounded-3xl border bg-card hover:shadow-xl transition-all gap-4 md:gap-0">
                <div className="h-12 w-12 md:h-16 md:w-16 shrink-0 bg-primary/10 rounded-xl md:rounded-2xl flex items-center justify-center md:mb-6">
                  <ShieldCheck className="h-6 w-6 md:h-8 md:w-8 text-primary" />
                </div>
                <div className="text-left md:text-center">
                  <h3 className="text-base md:text-xl font-bold mb-1 md:mb-4">Fully Vetted Pros</h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-snug">Every cleaner undergoes a rigorous background check and in-person interview process.</p>
                </div>
              </div>
              <div className="flex flex-row md:flex-col items-center md:text-center p-4 md:p-8 rounded-2xl md:rounded-3xl border bg-card hover:shadow-xl transition-all gap-4 md:gap-0">
                <div className="h-12 w-12 md:h-16 md:w-16 shrink-0 bg-primary/10 rounded-xl md:rounded-2xl flex items-center justify-center md:mb-6">
                  <Clock className="h-6 w-6 md:h-8 md:w-8 text-primary" />
                </div>
                <div className="text-left md:text-center">
                  <h3 className="text-base md:text-xl font-bold mb-1 md:mb-4">60-Second Booking</h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-snug">Our seamless online platform lets you book and manage your cleanings in seconds.</p>
                </div>
              </div>
              <div className="flex flex-row md:flex-col items-center md:text-center p-4 md:p-8 rounded-2xl md:rounded-3xl border bg-card hover:shadow-xl transition-all gap-4 md:gap-0">
                <div className="h-12 w-12 md:h-16 md:w-16 shrink-0 bg-primary/10 rounded-xl md:rounded-2xl flex items-center justify-center md:mb-6">
                  <CheckCircle2 className="h-6 w-6 md:h-8 md:w-8 text-primary" />
                </div>
                <div className="text-left md:text-center">
                  <h3 className="text-base md:text-xl font-bold mb-1 md:mb-4">200% Guarantee</h3>
                  <p className="text-sm md:text-base text-muted-foreground leading-snug">If you're not happy, we'll re-clean for free. If you're still not happy, you get your money back.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FAQ />
        <FinalCTA />
      </main>



      <Footer />
    </div>
  );
};

export default ServicesOverview;
